import { useState } from "react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { PaziaBox } from "components/atoms";
import { PaziaHeader } from "components/atoms/PaziaHeader/PaziaHeader";
import { monthNames, dayNamesShort } from "components/molecules/MonthCalendar";
import { TRAINER_AVAILABILITY_STATUS, TrainerAvailabilityRangeApi } from "api/trainers/trainerAvailabilityApi";

interface TrainerAvailabilityCalendarProps {
  ranges: TrainerAvailabilityRangeApi[];
  sessionDays: Date[];
}

type CellStatus = "available" | "unavailable" | "session" | "none";

const dateKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const getMonthGrid = (year: number, month: number): (Date | null)[] => {
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leadingBlanks = (firstDay.getDay() + 6) % 7; // Monday = 0

  const cells: (Date | null)[] = Array(leadingBlanks).fill(null);
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push(new Date(year, month, day));
  }
  return cells;
};

const getCellStatus = (day: Date, ranges: TrainerAvailabilityRangeApi[], sessionDays: Date[]): CellStatus => {
  const key = dateKey(day);

  if (sessionDays.some((s) => dateKey(new Date(s)) === key)) return "session";

  const range = ranges.find((r) => {
    const start = dateKey(new Date(r.startDate));
    const end = dateKey(new Date(r.endDate));
    return key >= start && key <= end;
  });
  if (!range) return "none";

  return range.status === TRAINER_AVAILABILITY_STATUS.AVAILABLE ? "available" : "unavailable";
};

const CELL_STYLES: Record<CellStatus, string> = {
  available: "bg-emerald-100 border-emerald-200 text-emerald-600 dark:bg-emerald-700 dark:border-emerald-600 dark:text-emerald-200",
  unavailable: "bg-amber-100 border-amber-200 text-amber-600 dark:bg-amber-700 dark:border-amber-600 dark:text-amber-200",
  session: "bg-indigo-100 border-indigo-200 text-indigo-600 dark:bg-indigo-700 dark:border-indigo-600 dark:text-indigo-200",
  none: "border-dashed border-slate-200 text-slate-400 dark:border-slate-700 dark:text-slate-500",
};

const CELL_LABELS: Record<CellStatus, string | null> = {
  available: "dostępna",
  unavailable: "urlop",
  session: "zajęcia",
  none: null,
};

const LEGEND: { status: CellStatus; label: string }[] = [
  { status: "available", label: "Dostępna" },
  { status: "unavailable", label: "Niedostępna" },
  { status: "session", label: "Zajęcia" },
  { status: "none", label: "Brak informacji" },
];

export const TrainerAvailabilityCalendar = ({ ranges, sessionDays }: TrainerAvailabilityCalendarProps) => {
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  const navigate = (direction: -1 | 1) => {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + direction, 1));
  };

  const cells = getMonthGrid(viewMonth.getFullYear(), viewMonth.getMonth());

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <PaziaHeader>{`${monthNames[viewMonth.getMonth()]} ${viewMonth.getFullYear()}`}</PaziaHeader>
        <div className="grid grid-flow-col justify-start gap-2 sm:auto-cols-max sm:justify-end">
          <button
            type="button"
            className="btn border-slate-200 bg-white px-2.5 text-slate-500 hover:border-slate-300 hover:text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:border-slate-600 dark:hover:text-slate-300"
            onClick={() => navigate(-1)}
          >
            <MdKeyboardArrowLeft size={20} />
          </button>
          <button
            type="button"
            className="btn border-slate-200 bg-white px-2.5 text-slate-500 hover:border-slate-300 hover:text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:border-slate-600 dark:hover:text-slate-300"
            onClick={() => navigate(1)}
          >
            <MdKeyboardArrowRight size={20} />
          </button>
        </div>
      </div>

      <PaziaBox className="p-4">
        <div className="grid grid-cols-7 gap-1">
          {dayNamesShort.slice(1).map((label) => (
            <div
              key={label}
              className="pb-1 text-center text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500"
            >
              {label}
            </div>
          ))}

          {cells.map((day, i) => {
            if (!day) return <div key={`blank-${i}`} className="h-14" />;

            const status = getCellStatus(day, ranges, sessionDays);
            return (
              <div
                key={day.toISOString()}
                className={`flex h-14 flex-col items-center justify-center rounded-md border text-sm font-semibold ${CELL_STYLES[status]}`}
              >
                {day.getDate()}
                {CELL_LABELS[status] && (
                  <span className="mt-0.5 text-[9px] font-bold uppercase tracking-wide">{CELL_LABELS[status]}</span>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 border-t border-slate-200 pt-4 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
          {LEGEND.map(({ status, label }) => (
            <span key={status} className="flex items-center gap-1.5">
              <span className={`h-3 w-3 rounded border ${CELL_STYLES[status]}`} />
              {label}
            </span>
          ))}
        </div>
      </PaziaBox>
    </div>
  );
};
